'use client';

import useSWR, { mutate, useSWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';
import { useCallback, useMemo } from 'react';
import { swrKeys, CACHE_TIMES, getAdminContextHeaders } from './config';
import { getBrowserClient } from '@/lib/integrations/supabase/client';
import { useAuth } from '@/lib/integrations/supabase';
import { normalizePhone, toE164, extractDigits, DEFAULT_COUNTRY_CODE } from '@/lib/utils/phone';
import { getDayRange } from '@/lib/utils/timezone';
import type {
  BookingWithDetails,
  Service,
  StaffMemberWithProfile,
  Alert,
  Customer,
  Business,
  BusinessHours,
  BusinessSettings,
  StaffHours,
  Payment,
  Expense,
} from '@/lib/integrations/supabase/types';

const supabaseFetcher = async <T>(
  query: () => Promise<{ data: T | null; error: Error | null }>
): Promise<T> => {
  const { data, error } = await query();
  if (error) throw error;
  if (!data) throw new Error('No data returned');
  return data;
};

function sortBookingsWithWalkInPriority(bookings: BookingWithDetails[]): BookingWithDetails[] {
  return [...bookings].sort((a, b) => {
    const timeA = new Date(a.start_at).getTime();
    const timeB = new Date(b.start_at).getTime();

    if (timeA !== timeB) {
      return timeA - timeB;
    }

    if (a.is_walk_in && !b.is_walk_in) return -1;
    if (!a.is_walk_in && b.is_walk_in) return 1;

    return 0;
  });
}

export function useBusiness(businessId: string | null) {
  return useSWRImmutable(
    businessId ? swrKeys.business(businessId) : null,
    async () => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('id', businessId!)
        .single();
      if (error) throw error;
      return data as Business;
    },
    {
      dedupingInterval: CACHE_TIMES.BUSINESS_DATA,
    }
  );
}

export function useBusinessHours(businessId: string | null) {
  return useSWRImmutable(
    businessId ? swrKeys.businessHours(businessId) : null,
    async () => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase
        .from('business_hours')
        .select('*')
        .eq('business_id', businessId!)
        .order('day_of_week', { ascending: true });
      if (error) throw error;
      return data as BusinessHours[];
    },
    {
      dedupingInterval: CACHE_TIMES.BUSINESS_DATA,
    }
  );
}

export function useBusinessSettings(businessId: string | null) {
  return useSWR(
    businessId ? swrKeys.businessSettings(businessId) : null,
    async () => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase
        .from('business_settings')
        .select('*')
        .eq('business_id', businessId!)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data as BusinessSettings | null;
    },
    {
      dedupingInterval: CACHE_TIMES.BUSINESS_DATA,
    }
  );
}

export function invalidateBusinessSettings(businessId: string) {
  mutate(swrKeys.businessSettings(businessId), undefined, { revalidate: true });
}

export function useStaffHours(staffId: string | null) {
  return useSWRImmutable(
    staffId ? swrKeys.staffHours(staffId) : null,
    async () => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase
        .from('staff_hours')
        .select('*')
        .eq('staff_id', staffId!)
        .order('day_of_week', { ascending: true });
      if (error) throw error;
      return data as StaffHours[];
    },
    {
      dedupingInterval: CACHE_TIMES.STATIC_DATA,
    }
  );
}

export function invalidateStaffHours(staffId: string) {
  mutate(swrKeys.staffHours(staffId), undefined, { revalidate: true });
}

export function useServices(businessId: string | null) {
  return useSWRImmutable(
    businessId ? swrKeys.services(businessId) : null,
    async () => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('business_id', businessId!)
        .eq('active', true)
        .order('name', { ascending: true });
      if (error) throw error;
      return data as Service[];
    },
    {
      dedupingInterval: CACHE_TIMES.STATIC_DATA,
    }
  );
}

export function useAllServices(businessId: string | null) {
  return useSWRImmutable(
    businessId ? swrKeys.allServices(businessId) : null,
    async () => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('business_id', businessId!)
        .order('name', { ascending: true });
      if (error) throw error;
      return data as Service[];
    },
    {
      dedupingInterval: CACHE_TIMES.STATIC_DATA,
    }
  );
}

export function useStaffMembers(businessId: string | null, includeOwner: boolean = false) {
  const cacheKey = businessId ? [...swrKeys.staff(businessId), includeOwner ? 'with-owner' : 'no-owner'] : null;

  return useSWR(
    cacheKey,
    async () => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase
        .from('staff_members')
        .select(`
          *,
          profile:profiles(*)
        `)
        .eq('business_id', businessId!)
        .eq('active', true)
        .order('display_name', { ascending: true });
      if (error) throw error;
      const filtered = (data || []).filter((staff: StaffMemberWithProfile) => {
        if (staff.profile?.role === 'admin') return false;
        if (staff.profile?.role === 'owner') return includeOwner;
        return true;
      });
      return filtered as StaffMemberWithProfile[];
    },
    {
      dedupingInterval: 5000,
      revalidateIfStale: true,
    }
  );
}

export function useAllStaffMembers(businessId: string | null) {
  return useSWR(
    businessId ? swrKeys.allStaff(businessId) : null,
    async () => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase
        .from('staff_members')
        .select(`
          *,
          profile:profiles(*)
        `)
        .eq('business_id', businessId!)
        .order('display_name', { ascending: true });
      if (error) throw error;
      return data as StaffMemberWithProfile[];
    },
    {
      dedupingInterval: 5000,
      revalidateIfStale: true,
    }
  );
}

export function useBookingsForDate(
  businessId: string | null,
  date: Date | null,
  staffId?: string | null
) {
  const dateStr = date ? (() => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  })() : null;

  return useSWR(
    businessId && dateStr ? swrKeys.bookingsForDate(businessId, dateStr, staffId) : null,
    async () => {
      const supabase = getBrowserClient();
      const startOfDay = new Date(date!);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date!);
      endOfDay.setHours(23, 59, 59, 999);

      let query = supabase
        .from('bookings')
        .select(`
          id, business_id, customer_id, staff_id, start_at, end_at, status,
          confirmation_status, notes, created_at, paid, is_walk_in,
          subtotal_aed, vat_rate, vat_amount_aed, total_aed,
          customer:customers(id, name, phone, email),
          staff_member:staff_members(id, display_name, display_name_ar, color_index),
          booking_services(id, service_id, price_aed_snapshot, quantity, original_price_aed,
            service:services(id, name, name_ar, duration_minutes))
        `)
        .eq('business_id', businessId!)
        .gte('start_at', startOfDay.toISOString())
        .lte('start_at', endOfDay.toISOString())
        .neq('status', 'cancelled')
        .order('start_at', { ascending: true });

      if (staffId) {
        query = query.eq('staff_id', staffId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as BookingWithDetails[];
    },
    {
      fallbackData: [],
      dedupingInterval: 10000,
      revalidateIfStale: true,
      revalidateOnMount: true,
      keepPreviousData: true,
    }
  );
}

export function useBookingsForDateRange(
  businessId: string | null,
  startDate: Date | null,
  endDate: Date | null,
  staffId?: string | null
) {
  const startStr = startDate?.toISOString() ?? null;
  const endStr = endDate?.toISOString() ?? null;

  return useSWR(
    businessId && startStr && endStr
      ? swrKeys.bookingsForDateRange(businessId, startStr, endStr, staffId)
      : null,
    async () => {
      const supabase = getBrowserClient();

      let query = supabase
        .from('bookings')
        .select(`
          id, business_id, customer_id, staff_id, start_at, end_at, status,
          confirmation_status, notes, created_at, paid, is_walk_in,
          subtotal_aed, vat_rate, vat_amount_aed, total_aed,
          customer:customers(id, name, phone, email),
          staff_member:staff_members(id, display_name, display_name_ar, color_index),
          booking_services(id, service_id, price_aed_snapshot, quantity, original_price_aed,
            service:services(id, name, name_ar, duration_minutes))
        `)
        .eq('business_id', businessId!)
        .gte('start_at', startDate!.toISOString())
        .lte('start_at', endDate!.toISOString())
        .neq('status', 'cancelled')
        .order('start_at', { ascending: true });

      if (staffId) {
        query = query.eq('staff_id', staffId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as BookingWithDetails[];
    },
    {
      fallbackData: [],
      dedupingInterval: 10000,
      revalidateIfStale: true,
      keepPreviousData: true,
    }
  );
}

export function useUpcomingBookings(businessId: string | null, limit: number = 5) {
  return useSWR(
    businessId ? swrKeys.upcomingBookings(businessId, limit) : null,
    async () => {
      const supabase = getBrowserClient();
      const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000).toISOString();
      const now = fifteenMinutesAgo;

      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id, business_id, customer_id, staff_id, start_at, end_at, status,
          confirmation_status, notes, created_at, paid, is_walk_in,
          subtotal_aed, vat_rate, vat_amount_aed, total_aed,
          customer:customers(id, name, phone, email),
          staff_member:staff_members(id, display_name, display_name_ar, color_index),
          booking_services(id, price_aed_snapshot, quantity, original_price_aed,
            service:services(id, name, name_ar, duration_minutes))
        `)
        .eq('business_id', businessId!)
        .gte('start_at', now)
        .in('status', ['pending', 'confirmed', 'in_queue', 'in_progress'])
        .order('start_at', { ascending: true })
        .limit(limit + 1);

      if (error) throw error;

      const bookings = data as BookingWithDetails[];
      const hasMore = bookings.length > limit;

      return {
        bookings: hasMore ? bookings.slice(0, limit) : bookings,
        totalCount: hasMore ? limit + 1 : bookings.length,
        hasMore,
      };
    },
    {
      dedupingInterval: 5000,
      revalidateOnMount: true,
      revalidateIfStale: true,
    }
  );
}

/**
 * Fetches due/overdue bookings - bookings where the scheduled time has passed
 * but the booking was not updated or checked in.
 * 15-minute grace period before marking as "due".
 */
export function useDueBookings(businessId: string | null, limit: number = 5) {
  return useSWR(
    businessId ? swrKeys.dueBookings(businessId, limit) : null,
    async () => {
      const supabase = getBrowserClient();
      const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id, business_id, customer_id, staff_id, start_at, end_at, status,
          confirmation_status, notes, created_at, paid, is_walk_in,
          subtotal_aed, vat_rate, vat_amount_aed, total_aed,
          customer:customers(id, name, phone, email),
          staff_member:staff_members(id, display_name, display_name_ar, color_index),
          booking_services(id, price_aed_snapshot, quantity, original_price_aed,
            service:services(id, name, name_ar, duration_minutes))
        `)
        .eq('business_id', businessId!)
        .lt('start_at', fifteenMinutesAgo)
        .in('status', ['pending', 'confirmed'])
        .order('start_at', { ascending: false })
        .limit(limit + 1);

      if (error) throw error;

      const bookings = data as BookingWithDetails[];
      const hasMore = bookings.length > limit;

      return {
        bookings: hasMore ? bookings.slice(0, limit) : bookings,
        totalCount: hasMore ? limit + 1 : bookings.length,
        hasMore,
      };
    },
    {
      dedupingInterval: 5000,
      revalidateOnMount: true,
      revalidateIfStale: true,
    }
  );
}

export interface BookedRange {
  start_at: string;
  end_at: string;
}

export function useBarberAvailability(
  businessId: string | null,
  staffId: string | null,
  date: Date | null
) {
  const dateStr = date?.toISOString().split('T')[0] ?? null;

  return useSWR(
    businessId && staffId && dateStr
      ? swrKeys.barberAvailability(businessId, staffId, dateStr)
      : null,
    async () => {
      const supabase = getBrowserClient();

      const dayStart = new Date(date!);
      dayStart.setHours(0, 0, 0, 0);

      const dayEnd = new Date(date!);
      dayEnd.setHours(23, 59, 59, 999);

      const { data, error } = await supabase
        .from('bookings')
        .select('start_at, end_at')
        .eq('business_id', businessId!)
        .eq('staff_id', staffId!)
        .lt('start_at', dayEnd.toISOString())
        .gt('end_at', dayStart.toISOString())
        .in('status', ['pending', 'confirmed', 'in_queue', 'in_progress'])
        .order('start_at', { ascending: true });

      if (error) throw error;
      return (data ?? []) as BookedRange[];
    },
    {
      dedupingInterval: 500,
      keepPreviousData: true,
    }
  );
}

export function useAlerts(businessId: string | null) {
  return useSWR(
    businessId ? swrKeys.alerts(businessId) : null,
    async () => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .eq('business_id', businessId!)
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return data as Alert[];
    },
    {
      dedupingInterval: 5000,
    }
  );
}

export function useTodayEarnings(businessId: string | null) {
  return useSWR(
    businessId ? swrKeys.todayEarnings(businessId) : null,
    async () => {
      const supabase = getBrowserClient();

      const todayRange = getDayRange('Asia/Dubai', 'today');
      const startOfDay = todayRange.start_utc;
      const endOfDay = todayRange.end_utc;

      type PaymentEarning = { amount_aed: number; method: string };

      const { data: paymentsData, error } = await supabase
        .from('payments')
        .select('amount_aed, method')
        .eq('business_id', businessId!)
        .eq('status', 'completed')
        .gte('created_at', startOfDay.toISOString())
        .lte('created_at', endOfDay.toISOString());

      if (error) throw error;

      const payments = (paymentsData ?? []) as PaymentEarning[];

      const earnings = { total: 0, cash: 0, card: 0 };
      for (const payment of payments) {
        const amount = Number(payment.amount_aed);
        earnings.total += amount;
        if (payment.method === 'cash') {
          earnings.cash += amount;
        } else if (payment.method === 'card') {
          earnings.card += amount;
        }
      }

      return earnings;
    },
    {
      dedupingInterval: 100,
      revalidateOnFocus: false,
      keepPreviousData: false,
    }
  );
}

export function useTodayEarningsForStaff(businessId: string | null, staffId: string | null) {
  return useSWR(
    businessId && staffId ? swrKeys.todayEarningsForStaff(businessId, staffId) : null,
    async () => {
      const supabase = getBrowserClient();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)('get_staff_earnings_today', {
        p_staff_id: staffId,
        p_business_id: businessId,
      });

      if (error) throw error;

      const result = Array.isArray(data) && data.length > 0 ? data[0] : null;

      return {
        total: Number(result?.total_amount ?? 0),
        cash: Number(result?.cash_amount ?? 0),
        card: Number(result?.card_amount ?? 0),
      };
    },
    {
      dedupingInterval: 100,
      revalidateOnFocus: false,
      keepPreviousData: false,
    }
  );
}

export type SalesTransaction = {
  id: string;
  amount_aed: number;
  method: string;
  status: string;
  created_at: string;
  notes?: string | null;
  booking?: {
    id?: string;
    is_walk_in?: boolean;
    customer?: { name: string; phone?: string | null } | null;
    staff_id?: string | null;
    staff_member?: { display_name: string; display_name_ar: string | null } | null;
    booking_services?: Array<{
      id: string;
      quantity: number;
      price_aed_snapshot: number;
      service?: { name: string; name_ar: string | null } | null;
    }> | null;
  } | null;
  discount_amount_aed?: number | null;
  original_amount_aed?: number | null;
};

export type SalesSummaryData = {
  totalRevenue: number;
  cardPayments: number;
  cashPayments: number;
  transferPayments: number;
  transactionCount: number;
  transactions: SalesTransaction[];
  cacheStatus: 'HIT' | 'MISS';
  cachedAt?: string;
};

type SalesRange = 'today' | 'week' | 'month' | 'year';
export type { SalesRange };

function getSalesDateRange(range: SalesRange): { start: Date; end: Date } {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  const start = new Date(now);

  if (range === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (range === 'week') {
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (range === 'month') {
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (range === 'year') {
    start.setDate(start.getDate() - 364);
    start.setHours(0, 0, 0, 0);
  }

  return { start, end };
}

export function useSalesSummary(
  businessId: string | null,
  range: SalesRange = 'today',
  staffId?: string | null,
  fallbackData?: SalesSummaryData
) {
  const dateKey = useMemo(() => new Date().toISOString().split('T')[0], []);
  const staffKey = staffId || 'all';
  const swrKey = businessId ? [...swrKeys.salesSummary(businessId, range, dateKey), staffKey] : null;

  return useSWR<SalesSummaryData>(
    swrKey,
    async () => {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      let url = `/api/sales/summary?range=${range}&businessId=${businessId}`;
      if (staffId) {
        url += `&staffId=${staffId}`;
      }
      const response = await fetch(url, {
        cache: 'no-store',
        headers: {
          ...getAdminContextHeaders(),
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to fetch sales' }));
        throw new Error(error.error || error.message || `Failed to fetch sales summary (${response.status})`);
      }

      const data = await response.json();

      const result: SalesSummaryData = {
        totalRevenue: data.totalRevenue ?? 0,
        cardPayments: data.cardPayments ?? 0,
        cashPayments: data.cashPayments ?? 0,
        transferPayments: data.transferPayments ?? 0,
        transactionCount: data.transactionCount ?? 0,
        transactions: (data.transactions ?? []).slice(0, 20),
        cacheStatus: data.cacheStatus ?? 'MISS',
      };

      return result;
    },
    {
      fallbackData,
      keepPreviousData: true,
      dedupingInterval: 5000,
      refreshInterval: 0,
      revalidateOnFocus: false,
      revalidateIfStale: true,
      revalidateOnMount: true,
    }
  );
}

export function invalidateSalesSummary(businessId: string) {
  mutate(
    (key) => Array.isArray(key) && key[0] === 'sales' && key[1] === 'summary' && key[2] === businessId
  );
}

import { getCustomersWithActivity, getCustomers } from '@/lib/features/data/customers';

export function useCustomers(businessId: string | null) {
  return useSWR(
    businessId ? swrKeys.customers(businessId) : null,
    async () => {
      return getCustomersWithActivity(businessId!);
    },
    {
      dedupingInterval: 5000,
    }
  );
}

export function useCustomerSearch(businessId: string | null, query: string) {
  return useSWR(
    businessId && query.length >= 2
      ? swrKeys.customerSearch(businessId, query)
      : null,
    async () => {
      const supabase = getBrowserClient();

      const searchConditions = [`name.ilike.%${query}%`, `email.ilike.%${query}%`];

      const digits = extractDigits(query);
      if (digits && digits.length >= 3) {
        const e164Phone = toE164(query, DEFAULT_COUNTRY_CODE);
        if (e164Phone) {
          searchConditions.push(`normalized_phone.eq.${e164Phone}`);
        }

        searchConditions.push(`normalized_phone.ilike.%${digits}`);

        if (digits.length >= 4) {
          searchConditions.push(`normalized_phone.ilike.%${digits}%`);
        }
      }

      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('business_id', businessId!)
        .or(searchConditions.join(','))
        .limit(10);

      if (error) throw error;
      return data as Customer[];
    },
    {
      dedupingInterval: CACHE_TIMES.SEARCH,
      keepPreviousData: true,
    }
  );
}

export function invalidateCustomers(businessId: string) {
  mutate(swrKeys.customers(businessId));
}

export function invalidateCustomerOffers(businessId: string, customerId: string | null) {
  if (customerId) {
    mutate(swrKeys.customerOffers(businessId, customerId));
  }
  mutate(swrKeys.customerOffers(businessId, 'general'));

  mutate(
    (key) => {
      if (!Array.isArray(key)) return false;
      return key[0] === 'customerOffers' && key[1] === businessId;
    },
    undefined,
    { revalidate: true }
  );
}

export function useCustomerOffers(businessId: string | null, customerId: string | null) {
  const { session } = useAuth();

  return useSWR(
    businessId && session?.access_token ? swrKeys.customerOffers(businessId, customerId || 'general') : null,
    async () => {
      const { getSession } = await import('@/lib/integrations/supabase/client');
      const currentSession = await getSession();

      if (!currentSession?.access_token) {
        throw new Error('Not authenticated');
      }

      const url = customerId
        ? `/api/offers/get?businessId=${businessId}&customerId=${customerId}`
        : `/api/offers/get?businessId=${businessId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`,
          ...getAdminContextHeaders(),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch offers');
      }
      const data = await response.json();
      return data.offers || [];
    },
    {
      dedupingInterval: CACHE_TIMES.SEARCH,
      keepPreviousData: true,
      revalidateIfStale: true,
      revalidateOnFocus: false,
    }
  );
}

import { getInboxMessages, getUnreadMessageCount } from '@/lib/features/data/inbox';
import type { InboxMessage } from '@/lib/integrations/supabase/types';

export function useInboxMessages(businessId: string | null, limit: number = 50) {
  return useSWR(
    businessId ? swrKeys.inboxMessages(businessId) : null,
    async () => {
      return getInboxMessages(businessId!, limit);
    },
    {
      dedupingInterval: 5000,
    }
  );
}

export function useUnreadCount(businessId: string | null) {
  return useSWR(
    businessId ? swrKeys.unreadCount(businessId) : null,
    async () => {
      return getUnreadMessageCount(businessId!);
    },
    {
      dedupingInterval: 5000,
    }
  );
}

export function invalidateInbox(businessId: string) {
  mutate(swrKeys.inboxMessages(businessId));
  mutate(swrKeys.unreadCount(businessId));
}

export function invalidateBookings(businessId: string) {
  mutate(
    (key) => {
      if (!Array.isArray(key)) return false;
      return key[0] === 'bookings' && key.includes(businessId);
    },
    undefined,
    { revalidate: false }
  );

  const commonLimits = [5, 10, 20, 50];
  commonLimits.forEach(limit => {
    mutate(swrKeys.upcomingBookings(businessId, limit), undefined, { revalidate: false });
    mutate(swrKeys.dueBookings(businessId, limit), undefined, { revalidate: false });
  });

  Promise.resolve().then(() => {
    mutate(
      (key) => {
        if (!Array.isArray(key)) return false;
        return key[0] === 'bookings' && key.includes(businessId);
      }
    );
    commonLimits.forEach(limit => {
      mutate(swrKeys.upcomingBookings(businessId, limit));
      mutate(swrKeys.dueBookings(businessId, limit));
    });
  });
}

export function invalidateEarnings(businessId: string) {
  const key = swrKeys.todayEarnings(businessId);
  mutate(key);

  mutate(
    (k) => Array.isArray(k) && k[0] === 'earnings' && k.includes(businessId)
  );
}

export function invalidateAlerts(businessId: string) {
  mutate(swrKeys.alerts(businessId));
}

export function invalidatePayments(businessId: string) {
  mutate(
    (key) => Array.isArray(key) && key[0] === 'payments' && key.includes(businessId),
    undefined,
    { revalidate: true }
  );
}

export function invalidateServices(businessId: string) {
  mutate(swrKeys.services(businessId), undefined, { revalidate: true });
  mutate(swrKeys.allServices(businessId), undefined, { revalidate: true });
}

export function invalidateStaff(businessId: string) {
  mutate(swrKeys.staff(businessId), undefined, { revalidate: true });
  mutate(swrKeys.allStaff(businessId), undefined, { revalidate: true });
}

export function invalidateBusinessHours(businessId: string) {
  mutate(swrKeys.businessHours(businessId), undefined, { revalidate: true });
}

export function useExpenses(
  businessId: string | null,
  dateRange?: { start: string; end: string }
) {
  const key = businessId && dateRange
    ? `expenses-${businessId}-${dateRange.start}-${dateRange.end}`
    : businessId
    ? `expenses-${businessId}`
    : null;

  return useSWR<Expense[]>(
    key,
    async () => {
      const supabase = getBrowserClient();
      let query = supabase
        .from('expenses')
        .select('*')
        .eq('business_id', businessId!)
        .order('expense_date', { ascending: false });

      if (dateRange) {
        query = query
          .gte('expense_date', dateRange.start)
          .lte('expense_date', dateRange.end);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Expense[];
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: CACHE_TIMES.STATIC_DATA,
    }
  );
}

export function invalidateExpenses(businessId: string) {
  mutate(`expenses-${businessId}`);
}

export function useCacheInvalidation() {
  return {
    invalidateBookings,
    invalidateEarnings,
    invalidateAlerts,
    invalidateServices,
    invalidateStaff,
    invalidatePayments,
    invalidateSalesSummary,
    invalidateCustomers,
    invalidateInbox,
    invalidateBusinessHours,
    invalidateExpenses,
    invalidatePlatformSettings,
  };
}

import type { PlatformSettings } from '@/lib/integrations/supabase/types';

export function usePlatformSettings() {
  return useSWR<PlatformSettings>(
    swrKeys.platformSettings(),
    async () => {
      const response = await fetch('/api/admin/platform-settings');
      if (!response.ok) {
        throw new Error('Failed to fetch platform settings');
      }
      const data = await response.json();
      return data.settings as PlatformSettings;
    },
    {
      dedupingInterval: CACHE_TIMES.BUSINESS_DATA,
      revalidateOnFocus: false,
      fallbackData: {
        ai_features_enabled: true,
        debug_tools_enabled: false,
      },
    }
  );
}

export function invalidatePlatformSettings() {
  mutate(swrKeys.platformSettings(), undefined, { revalidate: true });
}
